import { Bouton } from '../ui/composants/bouton'
import { BookOpen } from 'lucide-react'
import { EnteteSection } from '../ui/composants/entete-section'
import { useLiveQuery } from 'dexie-react-hooks'
import { useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { articlesAvecTva, passerArticlesSansTva } from '../lib/bibliotheque'
import { TAUX_TVA } from '../lib/devis'
import { nouvelId } from '../lib/id'
import { centimesVersEuros, eurosVersCentimes, formatEuro, lireNombre, prixVenteDepuisAchat, saisieEuros, versSaisie } from '../lib/money'
import { rechercher } from '../lib/recherche'
import { UNITES } from '../lib/unites'
import { getBase, type ArticleLocal } from '../outbox/base'
import { creer, lister, modifier, supprimer } from '../outbox/depot'
import { useFranchiseTva } from '../ui/franchise'
import { BarreActions, CHAMP } from '../ui/synchro'
import { ChampRecherche } from '../ui/composants/champ-recherche'

const euros = (cents: number | undefined) => (cents === undefined ? '' : saisieEuros(cents))

/**
 * Fiche article : prix d'achat × coefficient de marge = prix de vente. Le prix
 * de vente reste modifiable à la main (il fait foi dans les devis).
 */
function FicheArticle(props: { article: ArticleLocal | null; fermer: () => void; franchise: boolean }) {
  const base = getBase()
  const a = props.article
  const [designation, setDesignation] = useState(a?.designation ?? '')
  const [reference, setReference] = useState(a?.reference ?? '')
  const [unite, setUnite] = useState(a?.unite ?? 'u')
  const [achat, setAchat] = useState(euros(a?.prixAchatHtCents))
  const [coef, setCoef] = useState(a?.coefficientMarge !== undefined ? versSaisie(a.coefficientMarge) : '')
  const [vente, setVente] = useState(euros(a?.prixVenteHtCents))
  // En franchise, l'article est enregistré à 0 % même si l'ancien taux était différent
  const [tva, setTva] = useState(props.franchise ? 0 : (a?.tvaPercent ?? 10))

  const achatCents = achat.trim() ? eurosVersCentimes(achat) : undefined
  const coefNombre = coef.trim() ? lireNombre(coef) : undefined
  const venteCents = eurosVersCentimes(vente)
  const coefInvalide = coefNombre === null || (coefNombre !== undefined && !coefNombre.isPositive())
  const valide = designation.trim() !== '' && unite.trim() !== '' && venteCents !== null && achatCents !== null && !coefInvalide

  // Le prix de vente suit l'achat et le coefficient dès que les deux sont valides
  function recalculer(nouvelAchat: string, nouveauCoef: string) {
    const c = eurosVersCentimes(nouvelAchat)
    const k = lireNombre(nouveauCoef)
    if (c !== null && k?.isPositive()) setVente(euros(prixVenteDepuisAchat(c, k)))
  }

  async function enregistrer(e: FormEvent) {
    e.preventDefault()
    if (!valide) return
    const contenu = {
      designation: designation.trim(),
      reference: reference.trim() || undefined,
      unite: unite.trim(),
      prixAchatHtCents: achatCents,
      coefficientMarge: coefNombre?.toNumber(),
      prixVenteHtCents: venteCents,
      tvaPercent: tva,
    }
    if (a) await modifier(base, 'articles', a.id, contenu)
    else await creer(base, 'articles', { id: nouvelId(), ...contenu })
    props.fermer()
  }

  async function retirer() {
    if (!a || !confirm(`Retirer « ${a.designation} » de la bibliothèque ? Les devis existants ne changent pas.`)) return
    await supprimer(base, 'articles', a.id)
    props.fermer()
  }

  return (
    <form onSubmit={enregistrer} className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <h3 className="font-bold">{a ? 'Modifier l’article' : 'Nouvel article'}</h3>
      <label className="flex flex-col gap-1 text-sm">
        <span className="text-slate-700">Désignation</span>
        <input className={CHAMP} value={designation} onChange={(e) => setDesignation(e.target.value)} autoFocus={!a} />
      </label>
      <div className="grid grid-cols-2 gap-2 text-sm">
        <label className="flex flex-col gap-1">
          <span className="text-slate-700">Référence</span>
          <input className={CHAMP} value={reference} onChange={(e) => setReference(e.target.value)} />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-slate-700">Unité</span>
          <input className={CHAMP} value={unite} onChange={(e) => setUnite(e.target.value)} list="unites-biblio" />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-slate-700">Prix d'achat HT (€)</span>
          <input
            className={`${CHAMP} ${achatCents === null ? 'border-red-700' : ''}`}
            inputMode="decimal"
            value={achat}
            onChange={(e) => {
              setAchat(e.target.value)
              recalculer(e.target.value, coef)
            }}
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-slate-700">Coefficient de marge</span>
          <input
            className={`${CHAMP} ${coefInvalide ? 'border-red-700' : ''}`}
            inputMode="decimal"
            placeholder="ex. 1,35"
            value={coef}
            onChange={(e) => {
              setCoef(e.target.value)
              recalculer(achat, e.target.value)
            }}
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-slate-700">Prix de vente HT (€)</span>
          <input
            className={`${CHAMP} ${vente.trim() && venteCents === null ? 'border-red-700' : ''}`}
            inputMode="decimal"
            value={vente}
            onChange={(e) => setVente(e.target.value)}
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-slate-700">TVA</span>
          <select className={`${CHAMP} bg-white`} value={tva} disabled={props.franchise} onChange={(e) => setTva(Number(e.target.value))}>
            {(TAUX_TVA.includes(tva as never) ? TAUX_TVA : [...TAUX_TVA, tva]).map((t) => (
              <option key={t} value={t}>
                {versSaisie(t)} %
              </option>
            ))}
          </select>
        </label>
      </div>
      <div className="flex gap-2">
        <Bouton type="button" variante="secondaire" className="flex-1" onClick={props.fermer}>
          Annuler
        </Bouton>
        <Bouton type="submit" className="flex-1" disabled={!valide}>
          Enregistrer
        </Bouton>
      </div>
      {a && (
        <Bouton type="button" variante="lienDanger" className="self-start text-sm" onClick={retirer}>
          Retirer de la bibliothèque
        </Bouton>
      )}
      <datalist id="unites-biblio">
        {UNITES.map((u) => (
          <option key={u} value={u} />
        ))}
      </datalist>
    </form>
  )
}

export default function Bibliotheque() {
  const base = getBase()
  const articles = useLiveQuery(
    async () => (await lister(base, 'articles')).sort((a, b) => a.designation.localeCompare(b.designation, 'fr')),
    [],
  )
  const franchise = useFranchiseTva()
  const nbAvecTva = franchise ? articlesAvecTva(articles ?? []).length : 0
  const [recherche, setRecherche] = useState('')
  // null : fiche fermée ; 'nouveau' : création ; sinon l'article modifié
  const [fiche, setFiche] = useState<ArticleLocal | 'nouveau' | null>(null)
  const trouves = rechercher(articles ?? [], recherche, (a) => [a.designation, a.reference])

  return (
    <>
      <main className="flex flex-1 flex-col gap-4 p-4">
        <EnteteSection Icone={BookOpen} titre="Ma bibliothèque" description="Vos ouvrages et articles, réutilisables dans vos devis." />
        {fiche ? (
          <FicheArticle key={fiche === 'nouveau' ? 'nouveau' : fiche.id} article={fiche === 'nouveau' ? null : fiche} fermer={() => setFiche(null)} franchise={franchise} />
        ) : (
          <>
            <ChampRecherche libelle="Rechercher un article" placeholder="Rechercher (désignation, référence)" value={recherche} onChange={setRecherche} />
            {nbAvecTva > 0 && (
              <div className="flex flex-col gap-2 rounded-2xl border-2 border-nuit bg-slate-100 p-3 text-sm" role="alert">
                <p>
                  Votre entreprise est en franchise en base de TVA : {nbAvecTva} article{nbAvecTva > 1 ? 's portent' : ' porte'} encore de la TVA.
                </p>
                <Bouton variante="secondaire" onClick={() => void passerArticlesSansTva(base)}>
                  Passer {nbAvecTva > 1 ? 'tous les articles' : 'l’article'} à 0 %
                </Bouton>
              </div>
            )}
            {articles?.length === 0 && (
              <p className="text-slate-700">
                Bibliothèque vide : ajoutez vos prestations et fournitures, <Link to="/donnees" className="underline">importez
                votre fichier Excel</Link>, ou cochez « Enregistrer dans ma bibliothèque » en saisissant une ligne libre de devis.
              </p>
            )}
            <ul className="flex flex-col gap-2">
              {trouves.map((a) => (
                <li key={a.id}>
                  <button
                    className="flex min-h-12 w-full items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm text-left"
                    onClick={() => setFiche(a)}
                  >
                    <span className="min-w-0">
                      <span className="block">{a.designation}</span>
                      {a.reference && <span className="block text-sm text-slate-700">{a.reference}</span>}
                    </span>
                    <span className="shrink-0 text-sm">
                      {formatEuro(centimesVersEuros(a.prixVenteHtCents))} / {a.unite}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}
      </main>

      <BarreActions>
        <Bouton className="flex-1" onClick={() => setFiche('nouveau')} disabled={fiche !== null}>
          + Article
        </Bouton>
      </BarreActions>
    </>
  )
}
